import React, { useState, useEffect } from 'react';
import { sortProductsFlat } from '../utils/inventorySorting';

// Light blue palette (matches the redesigned New/Edit Document pages)
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#6B7C93';
const C_BORDER = '#E3ECF5';
const PRIMARY = '#0077B6';

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: '#FFFFFF',
  border: `1px solid ${C_BORDER}`,
  borderRadius: 10,
  color: C_TEXT,
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.3s, box-shadow 0.3s',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const gradientButton = {
  background: PRIMARY,
  border: 'none',
  color: '#fff',
  padding: '12px 24px',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: 0.5,
  boxShadow: '0 4px 14px rgba(0, 119, 182, 0.3)',
  transition: 'transform 0.3s, box-shadow 0.3s',
};
const darkButton = {
  background: '#FFFFFF',
  border: '1px solid #E3ECF5',
  color: C_TEXT,
  padding: '12px 24px',
  borderRadius: 12,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.3s',
};

const getPriceForLevel = (product, level) => {
  if (!product) return 0;
  if (level === 'A') return parseFloat(product.priceA) || 0;
  if (level === 'B') return parseFloat(product.priceB) || 0;
  if (level === 'C') return parseFloat(product.priceC) || 0;
  return parseFloat(product.priceA) || 0;
};

function ProductPickerModal({ open, onClose, onAdd, products, categories, currentPriceLevel }) {
  const [pickerCategory, setPickerCategory] = useState('All');
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedProducts, setSelectedProducts] = useState({});

  // Reset internal state each time the modal opens
  useEffect(() => {
    if (open) {
      setPickerCategory('All');
      setPickerSearch('');
      setSelectedProducts({});
    }
  }, [open]);

  if (!open) return null;

  // Category options driven from the actual product data (canonical order from
  // the `categories` prop first, then any categories present in the data that
  // aren't in that list) so gate categories — imported with whatever string the
  // CSV used — are always selectable and return results.
  const present = new Set();
  products.forEach(p => { if (p.category) present.add(p.category.trim()); });
  const canonicalCats = (categories || []).filter(c => present.has(c));
  const extraCats = [...present].filter(c => !(categories || []).includes(c)).sort();
  const availableCategories = [...canonicalCats, ...extraCats];

  const pickerFilteredProducts = sortProductsFlat(
    products.filter(p => {
      if (p.isActive === false) return false;
      const matchCategory = pickerCategory === 'All' ||
        (p.category || '').trim().toLowerCase() === pickerCategory.trim().toLowerCase();
      const term = pickerSearch.trim().toLowerCase();
      const matchSearch = !term ||
        p.description?.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term);
      return matchCategory && matchSearch;
    })
  );

  const selectedCount = Object.values(selectedProducts).filter(Boolean).length;

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const handleAdd = () => {
    const selectedProductObjs = Object.keys(selectedProducts)
      .filter(k => selectedProducts[k])
      .map(id => products.find(p => p.id === id))
      .filter(Boolean);
    if (selectedProductObjs.length === 0) return;
    onAdd(selectedProductObjs);
    setSelectedProducts({});
  };

  // Inventory badge (🔴 OUT / 🟡 LOW) for tracked products. null when fine.
  const renderStockBadge = (product) => {
    if (!product?.trackInventory) return null;
    const stock = parseFloat(product.stockQty) || 0;
    const reorder = parseFloat(product.reorderPoint) || 0;
    const base = { padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, marginLeft: 8, whiteSpace: 'nowrap' };
    if (stock <= 0) return <span style={{ ...base, background: 'rgba(226, 75, 74, 0.12)', color: '#A32D2D' }}>🔴 OUT</span>;
    if (stock <= reorder) return <span style={{ ...base, background: 'rgba(239, 159, 39, 0.15)', color: '#854F0B' }}>🟡 LOW ({stock})</span>;
    return null;
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(3, 4, 94, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          border: `1px solid ${C_BORDER}`,
          borderRadius: 20,
          width: '100%', maxWidth: 900, maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(3, 4, 94, 0.18)',
          overflow: 'hidden',
          color: C_TEXT,
        }}
      >
        <style>{`.ppm-dark-select option { background: #FFFFFF; color: #03045E; }`}</style>

        {/* Top accent line */}
        <div style={{
          height: 2,
          background: PRIMARY,
        }} />

        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${C_BORDER}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h2 style={{ color: C_TEXT, fontSize: 20, fontWeight: 800, margin: 0 }}>🛒 Add Products</h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C_TEXT_3, fontSize: 24, lineHeight: 1, cursor: 'pointer' }}
          >✕</button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '16px 24px',
          borderBottom: `1px solid ${C_BORDER}`,
          display: 'flex', gap: 12, flexWrap: 'wrap',
        }}>
          <select
            value={pickerCategory}
            onChange={e => setPickerCategory(e.target.value)}
            className="ppm-dark-select"
            style={{ ...inputStyle, width: 220, cursor: 'pointer', flex: '0 0 auto' }}
            onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
          >
            <option value="All">All Categories</option>
            {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
            <input
              type="text"
              placeholder="Filter products..."
              value={pickerSearch}
              onChange={e => setPickerSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 40 }}
              onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
            />
            <span style={{
              position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
              color: C_TEXT_3, fontSize: 14, pointerEvents: 'none',
            }}>🔍</span>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '0 16px',
            background: '#E6F1FB',
            border: '1px solid #E3ECF5',
            borderRadius: 10,
            color: '#0077B6', fontSize: 13, fontWeight: 700,
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {selectedCount} selected
          </div>
        </div>

        {/* Products list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 24px' }}>
          {pickerFilteredProducts.length === 0 ? (
            <p style={{ color: C_TEXT_3, textAlign: 'center', padding: 40, margin: 0 }}>
              No products found{pickerCategory !== 'All' ? ' in this category' : ''}.
            </p>
          ) : (
            pickerFilteredProducts.map(product => {
              const isSelected = !!selectedProducts[product.id];
              return (
                <div
                  key={product.id}
                  onClick={() => toggleProductSelection(product.id)}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#CDE7F5'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#FFFFFF'; }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '12px 16px', marginBottom: 6,
                    background: isSelected ? '#E6F1FB' : '#FFFFFF',
                    border: `1px solid ${isSelected ? '#0077B6' : '#E3ECF5'}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleProductSelection(product.id)}
                    onClick={e => e.stopPropagation()}
                    style={{ accentColor: '#0077B6', width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      color: C_TEXT, fontWeight: 600, fontSize: 13, margin: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {product.description}
                    </p>
                    <p style={{ color: C_TEXT_3, fontSize: 11, margin: '2px 0 0 0' }}>
                      <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700 }}>{product.sku}</span>
                      {' · '}{product.category}{product.unit ? ` · ${product.unit}` : ''}
                      {renderStockBadge(product)}
                    </p>
                  </div>
                  <p style={{ color: '#0077B6', fontWeight: 700, fontSize: 14, margin: 0, flexShrink: 0 }}>
                    ${getPriceForLevel(product, currentPriceLevel).toFixed(2)}
                  </p>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${C_BORDER}`,
          display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap',
        }}>
          <button
            onClick={() => setSelectedProducts({})}
            disabled={selectedCount === 0}
            style={{
              ...darkButton,
              opacity: selectedCount === 0 ? 0.5 : 1,
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={e => { if (selectedCount > 0) e.currentTarget.style.background = '#CDE7F5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; }}
          >
            Clear Selection
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedCount === 0}
            style={{
              ...gradientButton,
              opacity: selectedCount === 0 ? 0.5 : 1,
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={e => {
              if (selectedCount > 0) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 119, 182, 0.45)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 119, 182, 0.3)';
            }}
          >
            ➕ Add Selected ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductPickerModal;
